import { normalizeRef } from '~/lib/outline/refs'
import { HEIGHT, WIDTH } from '~/lib/stage'

/** A shape's `fill` can be a source: `{ source, fit }`. `fit` is `{ mode, edge?, offset?, from?, to? }`; the modes are surfaceFrames' reference frames. */
export const FIT_MODES = ['edge', 'perspective', 'screen', 'stage', 'flow']

export const DEFAULT_FIT_MODE = 'screen'

/** This machine's camera: a preset cannot name a device. */
export const CAMERA_SOURCE = 'camera'

export const isSourceFill = fill => !!fill && typeof fill === 'object' && typeof fill.source === 'string'

export const sourceFill = (source, fit = { mode: DEFAULT_FIT_MODE }) => ({ source, fit })

/** A source-filled shape's fit as a reference onto its own outline. */
export const fillRef = obj => ({ mode: DEFAULT_FIT_MODE, ...obj.fill.fit, id: obj.id })

const FILL_TYPES = new Set(['rect', 'circle', 'path', 'brush'])

/** Does this object draw a source fill? */
export const drawsSourceFill = obj => FILL_TYPES.has(obj?.type) && isSourceFill(obj.fill)

/** Where a fill node sits: the whole stage, so a `stage` fit windows it. */
export const FILL_NODE_POSE = {
	x: 0,
	y: 0,
	width: WIDTH,
	height: HEIGHT,
	rotation: 0,
	scaleY: 1,
	pivotX: 0.5,
	pivotY: 0.5,
}

/** The sources the fills in `objects` reference. */
export const fillSourcesOf = objects => {
	const out = new Set()
	for (const id in objects) {
		const fill = objects[id]?.fill
		if (isSourceFill(fill)) out.add(fill.source)
	}
	return out
}

/** The ids of the objects whose fill is `source`, in map order. */
export const fillUsersOf = (objects, source) => {
	const out = []
	for (const id in objects) if (objects[id]?.fill?.source === source) out.push(id)
	return out
}

/** A surfaces reference's framing as a sparse fit: segment keys only where its mode reads them. */
export const fitFromRef = ref => {
	const { mode, edge, offset, from, to } = normalizeRef(ref)
	const fit = { mode }
	if ((mode === 'edge' || mode === 'perspective') && edge) fit.edge = edge
	if (mode === 'flow') {
		if (from !== undefined) fit.from = from
		if (to !== undefined) fit.to = to
	} else if (offset) fit.offset = offset
	return fit
}
